import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ChevronRight from '../../icons/ChevronRight';
import './Blog.css';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../../LanguagePrefix';
import { useNavigate } from 'react-router-dom';
import Pagination from '@mui/material/Pagination';

function Blog() {
    const { slug } = useParams();
    const { t } = useTranslation();
    const langPrefix = useLanguagePrefix();
    const navigate = useNavigate();

    const [articles, setArticles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [trendingArticles, setTrendingArticles] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(6);

    const generateSlug = (title) => {
        const slug = title
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-');

        return slug ? `${slug}/` : '';
    };

    const selectedArticle = useMemo(() => {
        if (!slug || articles.length === 0) return null;
        return articles.find((a) => {
            const articleSlug = generateSlug(a.title).replace(/\/$/, '');
            return articleSlug === slug.replace(/\/$/, '');
        });
    }, [articles, slug]);

    const getArticleContent = (article) => {
        const cleanLangPrefix = (langPrefix || '').replace(/\//g, '');

        if (!cleanLangPrefix || cleanLangPrefix === 'en') {
            return article.content;
        }

        return article.contentTranslations?.[cleanLangPrefix] || article.content;
    }

    const getArticleTitle = (article) => {
        const cleanLangPrefix = (langPrefix || '').replace(/\//g, '');

        if (!cleanLangPrefix || cleanLangPrefix === 'en') {
            return article.title;
        }
        console.log("langPrefix: ", cleanLangPrefix)
        return article.titleTranslations?.[cleanLangPrefix] || article.title;
    }

    const getArticleMetaTitle = (article) => {
        const cleanLangPrefix = (langPrefix || '').replace(/\//g, '');

        if (!cleanLangPrefix || cleanLangPrefix === 'en') {
            return article.metaTitle;
        }

        return article.metaTitleTranslations?.[cleanLangPrefix] || article.metaTitle;
    }

    const getArticleMetaDescription = (article) => {
        const cleanLangPrefix = (langPrefix || '').replace(/\//g, '');

        if (!cleanLangPrefix || cleanLangPrefix === 'en') {
            return article.metaDescription;
        }

        return article.metaDescriptionTranslations?.[cleanLangPrefix] || article.metaDescription;
    }

    const selectedArticleName = useMemo(() => {
        if (!slug || articles.length === 0) return null;
        const found = articles.find((a) => {
            const articleSlug = generateSlug(a.title).replace(/\/$/, '');
            return articleSlug === slug.replace(/\/$/, '');
        });
        return found ? getArticleTitle(found) : null;
    }, [articles, slug]);

    const formattedCategoryName = !selectedArticle && slug
        ? slug.replace(/-/g, ' ')
        : '';

    const selectedCategory = formattedCategoryName
        ? categories.find((c) => c.name.toLowerCase() === formattedCategoryName)
        : null;

    const generateCategorySlug = (categoryName) =>
        categoryName.toLowerCase().replace(/\s+/g, '-');

    useEffect(() => {
        const fetchPaginatedArticles = async () => {
            setLoading(true);
            try {
                const requestPayload = {
                    searchTerm,
                    pageSize: pageSize,
                    currentPage,
                };

                const res = await fetch('https://api.prostay.com/WebsiteKnowledgebase/get-blog-articles-paginated', {
                //const res = await fetch('https://localhost:7167/WebsiteKnowledgebase/get-blog-articles-paginated', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(requestPayload)
                });

                if (!res.ok) throw new Error('Failed to fetch paginated articles');

                const responseData = await res.json();

                setArticles(responseData.data);
                setCurrentPage(responseData.currentPage);
                setTotalPages(responseData.totalPages);

                const uniqueCategoryNames = [...new Set(responseData.data.map(a => a.categoryName))];
                const uniqueCategoryMeta = uniqueCategoryNames.map(name => {
                    const match = responseData.data.find(a => a.categoryName === name);
                    return {
                        name,
                        metaTitle: match?.categoryMetaTitle || name,
                        metaDescription: match?.categoryMetaDescription || '',
                        description: match?.categoryDescription
                    };
                });

                setCategories(uniqueCategoryMeta);
            } catch (err) {
                console.error('Error loading blog articles:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchPaginatedArticles();
    }, [searchTerm, currentPage]);


    useEffect(() => {
        const fetchTrendingArticles = async () => {
            try {
                
                const resTrending = await fetch('https://api.prostay.com/WebsiteKnowledgebase/get-trending-blog-articles');
            //    const resTrending = await fetch('https://localhost:7167/WebsiteKnowledgebase/get-trending-blog-articles');
                if (!resTrending.ok) throw new Error('Failed to fetch trending articles');
                const dataTrending = await resTrending.json();
                setTrendingArticles(dataTrending);
            } catch (err) {
                console.error('Error loading trending articles:', err);
            }
        };

        fetchTrendingArticles();
    }, []);


    const capitalizeWords = (str) => str.replace(/\b\w/g, (char) => char.toUpperCase());

    const getPreview = (htmlContent) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        let plainText = tempDiv.textContent || tempDiv.innerText || '';
        plainText = plainText.replace(/\[Fig_\d+\]/g, '');
        return plainText.length > 130 ? plainText.substring(0, 130).trim() + '...' : plainText.trim();
    };


    const [filteredArticles, setFilteredArticles] = useState([]);

    useEffect(() => {
        let filtered = articles;

        if (formattedCategoryName) {
            filtered = filtered.filter(article =>
                article.categoryName.toLowerCase() === formattedCategoryName.toLowerCase()
            );
        }

        if (searchTerm) {
            filtered = filtered.filter(article =>
                article.title.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        setFilteredArticles(filtered);
    }, [articles, formattedCategoryName, searchTerm]);

    const canonicalUrl = `https://www.prostay.com${langPrefix}/blog${slug ? `/${slug}/` : '/'}`;

    const pageTitle = selectedArticle
        ? getArticleMetaTitle(selectedArticle) || getArticleTitle(selectedArticle)
        : selectedCategory
            ? selectedCategory.metaTitle || selectedCategory.name
            : 'Prostay – Hotel Technology News & Tips Blog';

    const pageDescription = selectedArticle
        ? getArticleMetaDescription(selectedArticle) || getPreview(getArticleContent(selectedArticle))
        : selectedCategory
            ? selectedCategory.metaDescription || ''
            : 'Read hotel tech trends, expert tips, and news from the Prostay team. Learn how to optimize your operations and guest experience.';

    function formatDate(date) {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    }

    const handleCategoryClick = (catName) => {
        const slugified = catName.toLowerCase().replace(/\s+/g, '-');
        navigate(`/blog/${slugified}`);
    };


    useEffect(() => {
        if (!selectedArticle) return;
      
        const postContent = document.getElementById("post-content");
        const tocContainer = document.getElementById("toc");
      
        if (!postContent || !tocContainer) return;
      
        const headings = postContent.querySelectorAll("h1, h2, h3, h4, h5, h6");
        if (headings.length === 0) return;
      
        const toc = document.createElement("ol");
        toc.className = "toc-list";
      
        headings.forEach((heading) => {
            const text = heading.textContent?.trim();
            if (!text) return; 
          
            const level = parseInt(heading.tagName.substring(1));
            const id = heading.id || text.toLowerCase().replace(/[^\w]+/g, "-");
          
            if (!heading.id) heading.id = id;
          
            const li = document.createElement("li");
            li.className = `toc-item level-${level}`;
          
            const a = document.createElement("a");
            a.href = `#${id}`;
            a.textContent = text;
          
            a.addEventListener("click", (e) => {
                e.preventDefault();
                const target = document.getElementById(heading.id);
                if (target) {
                  const offset = 100;
                  const bodyRect = document.body.getBoundingClientRect().top;
                  const elementRect = target.getBoundingClientRect().top;
                  const elementPosition = elementRect - bodyRect;
                  const offsetPosition = elementPosition - offset;
          
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth",
                  });
                }
              });

            li.appendChild(a);
            toc.appendChild(li);
          });
          
      
        tocContainer.innerHTML = "";
        tocContainer.appendChild(toc);
      }, [selectedArticle]);
      

    
    return (
        <div className="blog">
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={canonicalUrl} />
                <link rel="alternate" href={"https://www.prostay.com/en" + `/blog${slug ? `/${slug}/` : '/'}`} />
                <link rel="alternate" href={"https://www.prostay.com/es" + `/blog${slug ? `/${slug}/` : '/'}`} />


                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={"https://www.prostay.com" + `/blog${slug ? `/${slug}/` : '/'}`} />
                <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

                <meta property="og:site_name" content="Prostay" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
                <meta name="twitter:site" content="@prostaypms" />
                <meta name="twitter:creator" content="@prostaypms" />

            </Helmet>

            <div className="background-image">
                <div className="background-image-start">
                    <div className="d-flex align-items-center gap-2 gap-md-3 breadcrumbs">
                        <a href={`${langPrefix}/`} style={{ textDecoration: 'none' }}><small>{t('Home')}</small></a>
                        <ChevronRight />
                        <a href={`${langPrefix}/blog/`} style={{ textDecoration: 'none' }}>
                            <small className={!selectedArticle && !selectedCategory ? 'text-primary' : ''}>{t('Blog')}</small>
                        </a>
                        {(selectedArticle || selectedCategory) && (
                            <>
                                <ChevronRight />
                                <small className="text-primary">{selectedArticle ? selectedArticleName : selectedCategory?.name}</small>
                            </>
                        )}
                    </div>
                    <div className="mt-4">
                        <h1>{selectedArticle ? getArticleTitle(selectedArticle) : selectedCategory?.name || t('Prostay Blog')}</h1>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="d-flex align-items-center justify-content-center mt-5">
                    <div className="spinner-container">
                        <div className="custom-spinner"></div>
                    </div>
                </div>
            ) : (
                <div className="d-flex flex-column align-items-center justify-content-center">
                    <div className='d-flex mt-4 mb-1 justify-content-end page-content'>
                        <div className='search-container'>
                            <input
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        setSearchTerm(searchInput);
                                        setCurrentPage(1);
                                    }
                                }}
                                placeholder='Search'
                            />

                            <div onClick={() => {
                                setSearchTerm(searchInput);
                                setCurrentPage(1);
                            }} style={{ cursor: 'pointer' }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M21 21L16.7 16.7M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="#7784A3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    <div className="content-grid page-content mt-2 mt-md-3">
                        <div className='col'>
                            <div className="white-content-card" style={{ height: 'fit-content' }}>
                                <div className="white-content-card p-0 d-none d-md-flex flex-column">
                                    <h5 className='m-0' style={{ fontFamily: 'Inter' }}>{t('Popular Categories')}</h5>
                                    <hr />
                                    {categories.map((cat) => {
                                        const slug = cat.name.toLowerCase().replace(/\s+/g, "-");
                                        const isActive = cat.name.toLowerCase() === formattedCategoryName;

                                        return (
                                            <a
                                                key={cat.name}
                                                href={`/blog/${slug}/`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    navigate(`/blog/${slug}/`);
                                                }}
                                                className={`subcategory-link w-100 ${isActive ? "active" : ""}`}
                                                style={{ textDecoration: "none" }}
                                            >
                                                {cat.name}
                                            </a>
                                        );
                                    })}
                                </div>
                                <div className='d-block d-md-none'>
                                    <h5 className='m-0' style={{ fontFamily: 'Inter' }}>{t('Popular Categories')}</h5>
                                    <hr />
                                    <div className='select-container w-100' style={{ position: 'relative' }}>
                                        <select
                                            className="form-select w-100"
                                            value={formattedCategoryName}
                                            onChange={(e) => handleCategoryClick(e.target.value)}
                                        >
                                            <option value="">{t('Select a category')}</option>
                                            {categories.map((cat) => (
                                                <option key={cat.name} value={cat.name}>
                                                    {cat.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="white-content-card mt-3" style={{ height: 'fit-content' }}>
                                <h5 className='m-0' style={{ fontFamily: 'Inter' }}>{t('Trending Post')}</h5>
                                <hr />
                                {
                                    trendingArticles?.map((article, index) =>
                                        <a className='d-flex gap-2 trending-article-card' style={{ textDecoration: 'none' }} href={`/blog/${generateSlug(article.title || '')}`}>
                                            <img src={article.headerImageUrl} />
                                            <div className='col'>
                                                <label>{formatDate(article.addedAt)}</label>
                                                <h3>{getArticleTitle(article)}</h3>
                                            </div>
                                        </a>
                                    )
                                }
                            </div>
                        </div>



                        <div className="white-content-card articles-card">

                            {selectedArticle ? (
                                <>
                                <h5>{t('Table of contents')}</h5>
                                    <nav id="toc" />
                                    <div id="post-content">
                                        <div className="article-content" dangerouslySetInnerHTML={{ __html: getArticleContent(selectedArticle) }} />
                                    </div>
                                   
                                </>
                            ) : (
                                <>
                                    {
                                        articles && articles.length > 0 ?
                                            <>
                                                <div className="articles-grid">
                                                    {articles.map(article => (
                                                        <a
                                                            style={{ textDecoration: 'none' }}
                                                            key={article.id}
                                                            className="d-flex flex-column article"
                                                            href={`/blog/${generateSlug(article.title || '')}`}>
                                                            <img src={article.headerImageUrl} />
                                                            <label className='mt-2'>{formatDate(article.addedAt)}</label>
                                                            <h3 className='mt-2 mb-1'>{getArticleTitle(article)}</h3>
                                                            <small>{getPreview(getArticleContent(article))}</small>
                                                        </a>
                                                    ))}
                                                </div>
                                                <div className="d-flex justify-content-center mt-4">
                                                    <Pagination
                                                        count={totalPages}
                                                        page={currentPage}
                                                        onChange={(event, value) => {
                                                            setCurrentPage(value);
                                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                                        }}
                                                        color="primary"
                                                    />
                                                </div>
                                            </>

                                            :
                                            <div className='d-flex justify-content-center'>
                                                <small>No blog posts found</small>
                                            </div>
                                    }
                                </>
                            )}
                        </div>
                    </div>
                    {
                        selectedCategory &&
                        <div className='d-flex mt-4 mb-1 justify-content-center '>
                            <div className='white-content-card page-content'>
                                <p className='m-0'>{selectedCategory?.description}</p>
                            </div>
                        </div>
                    }
                </div>
            )}
        </div>
    );
}
export default Blog;
